<div class="list-container" [ngClass]="{ 'dark-mode': isDarkMode() }">

  <!-- ================= PAGE HEADER ================= -->
  <header class="page-header">
    <div class="header-left">
      <div class="title-row">
        <div class="header-icon-wrapper">
          <mat-icon class="header-main-icon">table_chart</mat-icon>
        </div>
        <div>
          <div class="title-with-badge">
            <h1 class="main-title">{{ pageTitle }}</h1>
            <span class="badge-count">{{ totalRecords }}</span>
          </div>
          <p class="subtitle">{{ subtitle }}</p>
        </div>
      </div>
    </div>

    <div class="header-right">
      <button type="button" mat-flat-button class="new-request-btn" (click)="goToNewRequest()">
        <mat-icon>add</mat-icon>
        <span>New Routing Request</span>
      </button>
    </div>
  </header>

  <!-- ================= TABS: ACTIVE REQUESTS vs DRAFTS ================= -->
  <div class="view-tabs-bar">
    <div class="view-tabs-group">
      <button type="button" class="view-tab-btn" [class.active]="selectedTab === 'active'" (click)="setTab('active')">
        <mat-icon class="tab-btn-icon">dynamic_feed</mat-icon>
        <span class="tab-btn-label">{{ isMerakiUser ? 'Submitted Requests' : 'Active Requests' }}</span>
        <span class="tab-badge active-badge">{{ activeCount }}</span>
      </button>

      <button type="button" class="view-tab-btn" [class.active]="selectedTab === 'drafts'" (click)="setTab('drafts')">
        <mat-icon class="tab-btn-icon">edit_note</mat-icon>
        <span class="tab-btn-label">{{ isMerakiUser ? 'Customer Drafts' : 'My Drafts' }}</span>
        <span class="tab-badge draft-badge" [class.has-drafts]="draftCount > 0">{{ draftCount }}</span>
      </button>
    </div>
  </div>

  <!-- ================= FILTER TOOLBAR ================= -->
  <section class="filters-bar">
    <div class="search-box">
      <mat-icon class="search-icon">search</mat-icon>
      <input type="text" [(ngModel)]="searchTerm" (ngModelChange)="onSearchChange()"
        placeholder="Search by Request ID, Vessel, IMO, Client or Port..." class="search-input" />
      <button *ngIf="searchTerm" type="button" class="clear-search-btn" (click)="searchTerm = ''; onSearchChange()">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="dropdown-filters">
      <!-- Client Filter (Meraki Only) -->
      <div class="filter-item" *ngIf="isMerakiUser">
        <mat-icon class="filter-icon">business</mat-icon>
        <select [(ngModel)]="selectedClient" (change)="onFilterChange()" class="custom-select">
          <option value="ALL">All Clients ({{ clientOptions.length }})</option>
          <option *ngFor="let client of clientOptions" [value]="client">{{ client }}</option>
        </select>
      </div>

      <!-- Vessel Filter -->
      <div class="filter-item">
        <mat-icon class="filter-icon">directions_boat</mat-icon>
        <select [(ngModel)]="selectedVessel" (change)="onFilterChange()" class="custom-select">
          <option value="ALL">All Vessels ({{ vesselOptions.length }})</option>
          <option *ngFor="let vessel of vesselOptions" [value]="vessel">{{ vessel }}</option>
        </select>
      </div>

      <!-- Status Filter -->
      <div class="filter-item">
        <mat-icon class="filter-icon">filter_list</mat-icon>
        <select [(ngModel)]="selectedStatus" (change)="onFilterChange()" class="custom-select" [disabled]="selectedTab === 'drafts'">
          <option *ngFor="let opt of displayedStatusOptions" [value]="opt">{{ opt === 'ALL' ? 'All Statuses' : opt }}</option>
        </select>
      </div>
    </div>
  </section>

  <!-- ================= TABLE CONTENT ================= -->
  <div class="table-card">
    <div class="table-loading-bar" *ngIf="isLoading">
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    </div>

    <div class="table-responsive">
      <table class="requests-table">
        <thead>
          <tr>
            <th class="th-expand"></th>
            <th class="th-sortable" (click)="toggleSort('createdAt')">
              <div class="th-content">
                <span>Request ID</span>
                <mat-icon class="sort-icon" *ngIf="sortBy === 'createdAt'">
                  {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </mat-icon>
              </div>
            </th>
            <th *ngIf="isMerakiUser" class="th-sortable" (click)="toggleSort('clientName')">
              <div class="th-content">
                <span>Client</span>
                <mat-icon class="sort-icon" *ngIf="sortBy === 'clientName'">
                  {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </mat-icon>
              </div>
            </th>
            <th class="th-sortable" (click)="toggleSort('vesselName')">
              <div class="th-content">
                <span>Vessel / IMO</span>
                <mat-icon class="sort-icon" *ngIf="sortBy === 'vesselName'">
                  {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </mat-icon>
              </div>
            </th>
            <th>Voyage Route</th>
            <th class="th-sortable" (click)="toggleSort('departureDate')">
              <div class="th-content">
                <span>Departure</span>
                <mat-icon class="sort-icon" *ngIf="sortBy === 'departureDate'">
                  {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </mat-icon>
              </div>
            </th>
            <th class="th-sortable" (click)="toggleSort('status')">
              <div class="th-content">
                <span>Status</span>
                <mat-icon class="sort-icon" *ngIf="sortBy === 'status'">
                  {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </mat-icon>
              </div>
            </th>
            <th>Activity / SLA</th>
            <th class="th-actions">Actions</th>
          </tr>
        </thead>

        <tbody>
          <!-- Empty State -->
          <tr *ngIf="!isLoading && requestsList.length === 0">
            <td [attr.colspan]="isMerakiUser ? 9 : 8" class="empty-state-cell">
              <div class="empty-state-box">
                <mat-icon class="empty-illustration-icon">search_off</mat-icon>
                <h3>No Routing Requests Found</h3>
                <p>Try adjusting your search criteria or create a new routing request.</p>
                <button type="button" mat-flat-button class="new-request-btn mt-2" (click)="goToNewRequest()">
                  <mat-icon>add</mat-icon>
                  <span>Create Request</span>
                </button>
              </div>
            </td>
          </tr>

          <!-- Request Rows & Expandable Drawers -->
          <ng-container *ngFor="let item of requestsList">
            <tr class="request-row" [class.expanded-row]="item.isExpanded" (click)="onRowClick(item, $event)">

              <!-- Expand Chevron -->
              <td class="td-expand">
                <button type="button" class="expand-btn" [class.rotated]="item.isExpanded"
                  (click)="toggleRowExpansion(item, $event)" matTooltip="Toggle timeline & details">
                  <mat-icon>keyboard_arrow_right</mat-icon>
                </button>
              </td>

              <!-- Request ID -->
              <td class="td-id">
                <div class="id-wrapper">
                  <span class="req-id-pill">{{ item.id }}</span>
                  <span class="voyage-ref-tag" *ngIf="item.voyageRef">#{{ item.voyageRef }}</span>
                </div>
              </td>

              <!-- Client (Meraki Only) -->
              <td *ngIf="isMerakiUser" class="td-client">
                <div class="client-info">
                  <mat-icon class="client-icon">domain</mat-icon>
                  <span class="client-name" [title]="item.clientName">{{ item.clientName || 'Meraki Operations'
                    }}</span>
                </div>
              </td>

              <!-- Vessel & IMO -->
              <td class="td-vessel">
                <div class="vessel-info">
                  <div class="vessel-name-row">
                    <mat-icon class="vessel-row-icon">directions_boat</mat-icon>
                    <strong class="vessel-name">{{ item.vesselName || 'Unnamed Vessel' }}</strong>
                  </div>
                  <div class="vessel-sub-row">
                    <span class="imo-badge" *ngIf="item.imo">IMO {{ item.imo }}</span>
                    <span class="vtype-badge" *ngIf="item.vesselType">{{ item.vesselType }}</span>
                  </div>
                </div>
              </td>

              <!-- Route -->
              <td class="td-route">
                <div class="route-display">
                  <div class="ports-flow">
                    <span class="origin-port">{{ item.startPort || 'Origin' }}</span>
                    <mat-icon class="route-arrow">arrow_forward</mat-icon>
                    <span class="dest-port">{{ item.endPort || 'Destination' }}</span>
                  </div>
                  <div class="stops-tag" *ngIf="(item.totalStopsCount || 2) > 2">
                    <mat-icon class="stops-icon">pin_drop</mat-icon>
                    <span>+{{ (item.totalStopsCount || 2) - 2 }} waypoint{{ (item.totalStopsCount || 2) - 2 === 1 ? '' :
                      's' }}</span>
                  </div>
                </div>
              </td>

              <!-- Departure Date -->
              <td class="td-departure">
                <div class="departure-cell">
                  <span class="dep-date">{{ item.departureDate | date:'mediumDate' }}</span>
                  <span class="dep-time" *ngIf="item.departureTime">{{ item.departureTime }} UTC</span>
                </div>
              </td>

              <!-- Status -->
              <td class="td-status">
                <div class="status-pill" [ngClass]="getStatusClass(item.status)">
                  <span class="status-dot"></span>
                  <span class="status-text">{{ item.status }}</span>
                </div>
              </td>

              <!-- Dynamic Time Ago / Resolved In -->
              <td class="td-sla">
                <div class="time-cell" [class.resolved-time]="formatTimeAgoOrResolved(item).isResolved"
                  [class.draft-time]="item.status === 'Draft'">
                  <mat-icon class="time-icon">{{ formatTimeAgoOrResolved(item).isResolved ? 'check_circle' :
                    (item.status === 'Draft' ? 'save_as' : 'schedule') }}</mat-icon>
                  <span>{{ formatTimeAgoOrResolved(item).text }}</span>
                </div>
              </td>

              <!-- Actions -->
              <td class="td-actions" (click)="$event.stopPropagation()">
                <div class="action-btn-group">

                  <!-- 1. Continue Draft -->
                  <button *ngIf="item.status === 'Draft'" type="button" class="btn-action btn-continue-draft"
                    (click)="editRequest(item, $event)" matTooltip="Continue editing and submit draft">
                    <mat-icon>edit_note</mat-icon>
                    <span>Continue Draft</span>
                  </button>

                  <!-- 2. Meraki Operations Actions -->
                  <ng-container *ngIf="isMerakiUser">
                    <!-- Start Routing Action -->
                    <button
                      *ngIf="item.status === 'Requested' || item.status === 'Resubmitted' || item.status === 'Edit Requested'"
                      type="button" class="btn-action btn-start-routing" (click)="startRouting(item, $event)"
                      matTooltip="Mark as In Progress and begin routing analysis">
                      <mat-icon>play_arrow</mat-icon>
                      <span>Start Routing</span>
                    </button>

                    <!-- Ask Edit From Customer -->
                    <button
                      *ngIf="item.status !== 'Draft' && item.status !== 'Completed' && item.status !== 'Cancelled'"
                      type="button" class="btn-action btn-ask-edit" (click)="openCustomerEditModal(item, $event)"
                      matTooltip="Ask customer to revise/confirm parameters">
                      <mat-icon>contact_support</mat-icon>
                      <span>Ask Edit</span>
                    </button>

                    <!-- Edit Request Parameters -->
                    <button *ngIf="item.status !== 'Draft'" type="button" class="btn-action btn-edit-req"
                      (click)="editRequest(item, $event)" matTooltip="Correct typos or adjust parameters">
                      <mat-icon>edit</mat-icon>
                    </button>

                    <!-- Prefill Initial Route Report (Visible once Routing has Started) -->
                    <button
                      *ngIf="item.status === 'In Progress' || item.status === 'Created' || item.status === 'Completed'"
                      type="button" class="btn-action btn-prefill" (click)="openInInitialRouteReport(item, $event)"
                      matTooltip="Auto-fill Initial Route Report">
                      <mat-icon>input</mat-icon>
                      <span>Initial Form</span>
                    </button>
                  </ng-container>

                  <!-- 3. Customer Edit Action (When permitted) -->
                  <button *ngIf="!isMerakiUser && item.status === 'Edit Requested'" type="button"
                    class="btn-action btn-customer-edit" (click)="editRequest(item, $event)"
                    matTooltip="Review requested changes and re-submit">
                    <mat-icon>edit</mat-icon>
                    <span>Update Request</span>
                  </button>

                  <!-- 4. Download Report Action -->
                  <button *ngIf="item.status === 'Created' || item.status === 'Completed'" type="button"
                    class="btn-action btn-download" (click)="downloadReport(item, null, $event)"
                    matTooltip="Download Optimized Route PDF Report">
                    <mat-icon>picture_as_pdf</mat-icon>
                    <span>Report</span>
                  </button>

                  <!-- Expand Drawer Toggle Button -->
                  <button type="button" class="btn-action btn-timeline-toggle" (click)="toggleTimeline(item, $event)"
                    matTooltip="View full timeline & specifications">
                    <mat-icon>{{ item.isExpanded ? 'expand_less' : 'history' }}</mat-icon>
                  </button>

                </div>
              </td>
            </tr>

            <!-- ================= EXPANDABLE DRAWER ROW ================= -->
            <tr *ngIf="item.isExpanded" class="drawer-detail-row no-hover">
              <td [attr.colspan]="isMerakiUser ? 9 : 8" class="drawer-detail-cell">
                <div class="drawer-container">

                  <!-- Drawer Header & Tabs -->
                  <div class="drawer-header">
                    <div class="drawer-tabs">
                      <button type="button" class="drawer-tab-btn" [class.active]="activeDrawerTab === 'timeline'"
                        (click)="setDrawerTab('timeline')">
                        <mat-icon>history</mat-icon>
                        <span>Timeline & Milestones</span>
                        <span class="tab-badge" *ngIf="item.statusHistory">{{ item.statusHistory.length }}</span>
                      </button>

                      <button type="button" class="drawer-tab-btn" [class.active]="activeDrawerTab === 'details'"
                        (click)="setDrawerTab('details')">
                        <mat-icon>assignment</mat-icon>
                        <span>Voyage Specifications & Parameters</span>
                      </button>
                    </div>

                    <div class="drawer-header-actions">
                      <span class="drawer-req-tag">Request ID: <strong>{{ item.id }}</strong></span>
                      <button type="button" class="close-drawer-btn" (click)="closeDrawer(item, $event)">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  </div>

                  <!-- TAB 1: TIMELINE -->
                  <div class="drawer-body timeline-tab-body" *ngIf="activeDrawerTab === 'timeline'">

                    <!-- Customer Edit Banner Alert if Edit Requested -->
                    <div class="timeline-alert-card" *ngIf="item.status === 'Edit Requested'">
                      <div class="alert-icon-col">
                        <mat-icon>help_outline</mat-icon>
                      </div>
                      <div class="alert-content-col">
                        <h4>Clarification / Edit Requested by Meraki Operations</h4>
                        <p>Meraki operations requested clarification. Please review the note in the timeline below and
                          click 'Update Request' to submit revisions.</p>
                      </div>
                      <button *ngIf="!isMerakiUser" type="button" mat-flat-button class="alert-action-btn"
                        (click)="editRequest(item)">
                        <mat-icon>edit</mat-icon>
                        <span>Update Request Now</span>
                      </button>
                    </div>

                    <!-- Vertical Milestone Stepper -->
                    <div class="timeline-stepper">
                      <div class="timeline-event-item" *ngFor="let ev of item.statusHistory; let last = last"
                        [class.meraki-actor]="ev.role === 'meraki_ops'" [class.client-actor]="ev.role === 'client'"
                        [class.system-actor]="ev.role === 'system'">

                        <!-- Stepper Node Line -->
                        <div class="timeline-node-col">
                          <div class="timeline-node-dot" [ngClass]="ev.action?.toLowerCase()">
                            <mat-icon class="node-icon"
                              *ngIf="ev.action === 'REQUEST_SUBMITTED' || ev.action === 'DRAFT_SUBMITTED'">send</mat-icon>
                            <mat-icon class="node-icon"
                              *ngIf="ev.action === 'CUSTOMER_RESUBMITTED' || ev.action === 'RESUBMIT_EDIT'">published_with_changes</mat-icon>
                            <mat-icon class="node-icon" *ngIf="ev.action === 'DRAFT_SAVED'">save_as</mat-icon>
                            <mat-icon class="node-icon"
                              *ngIf="ev.action === 'STATUS_CHANGED' && ev.status === 'In Progress'">play_arrow</mat-icon>
                            <mat-icon class="node-icon"
                              *ngIf="ev.action === 'CUSTOMER_EDIT_REQUESTED'">contact_support</mat-icon>
                            <mat-icon class="node-icon" *ngIf="ev.action === 'MERAKI_EDITED'">edit</mat-icon>
                            <mat-icon class="node-icon" *ngIf="ev.action === 'CUSTOMER_EDITED'">history_edu</mat-icon>
                            <mat-icon class="node-icon"
                              *ngIf="ev.action === 'REPORT_GENERATED'">picture_as_pdf</mat-icon>
                            <mat-icon class="node-icon"
                              *ngIf="ev.action === 'REPORT_DOWNLOADED'">file_download</mat-icon>
                            <mat-icon class="node-icon"
                              *ngIf="!ev.action || ev.action === 'STATUS_CHANGED' && ev.status !== 'In Progress'">check</mat-icon>
                          </div>
                          <div class="timeline-line" *ngIf="!last"></div>
                        </div>

                        <!-- Event Content Card -->
                        <div class="timeline-card">
                          <div class="timeline-card-header">
                            <div class="event-title-group">
                              <h4 class="event-title">{{ ev.title || ev.action }}</h4>
                              <span class="actor-badge" [ngClass]="ev.role || 'client'">
                                {{ ev.actor || 'System' }}
                              </span>
                            </div>
                            <div class="event-time-group">
                              <span class="relative-time">{{ formatRelativeTime(ev.timestamp) }}</span>
                              <span class="exact-time">{{ ev.timestamp | date:'medium' }}</span>
                            </div>
                          </div>

                          <!-- Remarks text -->
                          <p class="event-remarks" *ngIf="ev.remarks">{{ ev.remarks }}</p>

                          <!-- Parameter Field Diffs (If Meraki or Customer edited) -->
                          <div class="diff-container" *ngIf="ev.changes && ev.changes.length > 0">
                            <span class="diff-header-title">Parameter Adjustments:</span>
                            <div class="diff-chips-list">
                              <div class="diff-chip" *ngFor="let change of ev.changes">
                                <span class="diff-field-name">{{ change.fieldLabel || change.field }}:</span>
                                <span class="diff-old-val">{{ change.old || 'None' }}</span>
                                <mat-icon class="diff-arrow">arrow_right_alt</mat-icon>
                                <span class="diff-new-val">{{ change.new }}</span>
                              </div>
                            </div>
                          </div>

                          <!-- Report Download Action (If report ready) -->
                          <div class="event-download-action" *ngIf="ev.action === 'REPORT_GENERATED'">
                            <button type="button" mat-stroked-button class="timeline-download-btn"
                              (click)="downloadReport(item, ev)">
                              <mat-icon>file_download</mat-icon>
                              <span>Download PDF Report</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- TAB 2: SPECIFICATIONS & PARAMETERS -->
                  <div class="drawer-body details-tab-body" *ngIf="activeDrawerTab === 'details'">
                    <div class="specs-grid">

                      <!-- Card 1: Route & Waypoints -->
                      <div class="spec-card">
                        <div class="spec-card-header">
                          <mat-icon class="spec-icon">alt_route</mat-icon>
                          <h4>Route Endpoints & Waypoints</h4>
                        </div>
                        <div class="spec-content">
                          <div class="spec-row">
                            <span class="spec-label">Departure Port:</span>
                            <span class="spec-val"><strong>{{ item.startPort }}</strong> ({{ item.startLat || '-' }}°{{
                              item.startLatDir || 'N' }}, {{ item.startLong || '-' }}°{{ item.startLongDir || 'E'
                              }})</span>
                          </div>
                          <div class="spec-row">
                            <span class="spec-label">Destination Port:</span>
                            <span class="spec-val"><strong>{{ item.endPort }}</strong> ({{ item.endLat || '-' }}°{{
                              item.endLatDir || 'N' }}, {{ item.endLong || '-' }}°{{ item.endLongDir || 'E' }})</span>
                          </div>
                          <div class="spec-row">
                            <span class="spec-label">Waypoints Count:</span>
                            <span class="spec-val"><strong>{{ (item.totalStopsCount || 2) - 2 }} Waypoint{{
                                (item.totalStopsCount || 2) - 2 === 1 ? '' : 's' }}</strong></span>
                          </div>
                          <div class="spec-row" *ngIf="item.itinerary && item.itinerary.length > 0">
                            <div class="mini-itinerary-list">
                              <span *ngFor="let stop of item.itinerary; let idx = index" class="mini-stop-pill">
                                <strong>WP {{ idx + 1 }}:</strong> {{ stop.port || stop.customPortName }} ({{
                                stop.operation }})
                              </span>
                            </div>
                          </div>
                          <div class="spec-row attached-csv-row" *ngIf="item.routeCsvName || item.rawPayload?.routeCsvName">
                            <span class="spec-label">Attached Route CSV:</span>
                            <div class="attached-csv-download-btn" (click)="downloadRouteCsv(item, $event)" matTooltip="Download attached route CSV">
                              <mat-icon class="csv-icon">table_chart</mat-icon>
                              <span class="csv-name">{{ item.routeCsvName || item.rawPayload?.routeCsvName }}</span>
                              <span class="csv-size" *ngIf="item.routeCsvSize || item.rawPayload?.routeCsvSize">({{ item.routeCsvSize || item.rawPayload?.routeCsvSize }})</span>
                              <mat-icon class="dl-icon">file_download</mat-icon>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Card 2: Commercial & Bunkers -->
                      <div class="spec-card">
                        <div class="spec-card-header">
                          <mat-icon class="spec-icon">monetization_on</mat-icon>
                          <h4>Commercial, Financial & Bunkers</h4>
                        </div>
                        <div class="spec-content">
                          <div class="spec-row">
                            <span class="spec-label">Daily Charter Rate:</span>
                            <span class="spec-val highlight-val">${{ item.dailyRate | number }}/day</span>
                          </div>
                          <div class="spec-row">
                            <span class="spec-label">EU ETS Carbon:</span>
                            <span class="spec-val">{{ item.etsPrice ? ('$' + item.etsPrice + '/tonne CO₂') : 'Exempt /
                              Not Set' }}</span>
                          </div>
                          <div class="spec-bunkers-row">
                            <span class="spec-label">Bunker Prices ($/MT):</span>
                            <div class="bunker-chips-list">
                              <span class="bunker-tag" *ngIf="item.vlsfoPrice || item.rawPayload?.vlsfo">VLSFO: ${{
                                item.vlsfoPrice || item.rawPayload?.vlsfo }}</span>
                              <span class="bunker-tag" *ngIf="item.lsmgoPrice || item.rawPayload?.lsmgo">LSMGO: ${{
                                item.lsmgoPrice || item.rawPayload?.lsmgo }}</span>
                              <span class="bunker-tag" *ngIf="item.hsfoPrice || item.rawPayload?.hsfo">HSFO: ${{
                                item.hsfoPrice || item.rawPayload?.hsfo }}</span>
                              <span class="bunker-tag" *ngIf="item.biofuelPrice || item.rawPayload?.biofuel">Biofuel:
                                ${{ item.biofuelPrice || item.rawPayload?.biofuel }}</span>
                              <span class="bunker-tag-empty"
                                *ngIf="!(item.vlsfoPrice || item.rawPayload?.vlsfo || item.lsmgoPrice || item.rawPayload?.lsmgo || item.hsfoPrice || item.rawPayload?.hsfo)">Standard
                                Market Bunkers</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Card 3: Routing Avoidances & Optimization -->
                      <div class="spec-card">
                        <div class="spec-card-header">
                          <mat-icon class="spec-icon">tune</mat-icon>
                          <h4>Routing Avoidances & Optimization</h4>
                        </div>
                        <div class="spec-content">
                          <div class="spec-row">
                            <span class="spec-label">Speed Mode:</span>
                            <span class="spec-val"><strong>{{ item.optimizationType }}</strong> ({{ item.speed || 12.5 }} kn)</span>
                          </div>
                          <div class="spec-row">
                            <span class="spec-label">Operating Condition:</span>
                            <span class="spec-val">
                              <span class="condition-badge" [class.condition-ballast]="(item.condition || item.rawPayload?.condition) === 'Ballast'">
                                <mat-icon>{{ (item.condition || item.rawPayload?.condition) === 'Ballast' ? 'water' : 'layers' }}</mat-icon>
                                {{ item.condition || item.rawPayload?.condition || 'Laden' }}
                              </span>
                            </span>
                          </div>
                          <div class="avoidance-badges-grid">
                            <span class="avoid-pill" [class.avoid-active]="item.avoidIce">
                              <mat-icon>{{ item.avoidIce ? 'check_circle' : 'cancel' }}</mat-icon>
                              Avoid Ice: <strong>{{ item.avoidIce ? 'YES' : 'NO' }}</strong>
                            </span>
                            <span class="avoid-pill" [class.avoid-active]="item.avoidHRA">
                              <mat-icon>{{ item.avoidHRA ? 'check_circle' : 'cancel' }}</mat-icon>
                              Avoid HRA: <strong>{{ item.avoidHRA ? 'YES' : 'NO' }}</strong>
                            </span>
                            <span class="avoid-pill" [class.avoid-active]="item.secaFuel">
                              <mat-icon>{{ item.secaFuel ? 'check_circle' : 'cancel' }}</mat-icon>
                              SECA / ECA: <strong>{{ item.secaFuel ? 'YES' : 'NO' }}</strong>
                            </span>
                            <span class="avoid-pill" [class.avoid-active]="item.tct">
                              <mat-icon>{{ item.tct ? 'check_circle' : 'cancel' }}</mat-icon>
                              TCT Trip: <strong>{{ item.tct ? 'YES' : 'NO' }}</strong>
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Card 4: Weather Margins & Safety Limits -->
                      <div class="spec-card">
                        <div class="spec-card-header">
                          <mat-icon class="spec-icon">shield</mat-icon>
                          <h4>Weather Margin & Safety Thresholds</h4>
                        </div>
                        <div class="spec-content">
                          <div class="spec-row">
                            <span class="spec-label">Weather Margin:</span>
                            <span class="status-badge-yes" *ngIf="item.weatherRestriction !== false">
                              <mat-icon>check_circle</mat-icon> YES (Applied Restrictions)
                            </span>
                            <span class="status-badge-no" *ngIf="item.weatherRestriction === false">
                              <mat-icon>cancel</mat-icon> NO (Ignored Constraints)
                            </span>
                          </div>
                          <div class="spec-row">
                            <span class="spec-label">Max Wind Threshold:</span>
                            <span class="spec-val">{{ (item.maxWind != null ? item.maxWind : (item.rawPayload?.maxWind != null ? item.rawPayload?.maxWind : '—')) }} knots</span>
                          </div>
                          <div class="spec-row">
                            <span class="spec-label">Max Significant Wave:</span>
                            <span class="spec-val">{{ (item.maxWave != null ? item.maxWave : (item.rawPayload?.maxWave != null ? item.rawPayload?.maxWave : '—')) }} meters</span>
                          </div>
                          <div class="spec-row">
                            <span class="spec-label">UKC Draft Margin:</span>
                            <span class="spec-val">{{ (item.underKeel != null ? item.underKeel : (item.rawPayload?.underKeel != null ? item.rawPayload?.underKeel : '—')) }} meters</span>
                          </div>
                          <div class="spec-row" *ngIf="item.routingRemarks">
                            <span class="spec-label">Routing Notes:</span>
                            <span class="spec-val remarks-preview">{{ item.routingRemarks }}</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>

                </div>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>

    <!-- Paginator -->
    <mat-paginator [length]="totalRecords" [pageSize]="pageSize" [pageIndex]="pageIndex"
      [pageSizeOptions]="pageSizeOptions" (page)="onPageChange($event)" class="custom-paginator">
    </mat-paginator>
  </div>

  <!-- ================= ASK CUSTOMER EDIT MODAL ================= -->
  <div class="modal-backdrop" *ngIf="isRequestingEditModalOpen">
    <div class="modal-card">
      <div class="modal-header">
        <div class="modal-title-wrap">
          <mat-icon class="modal-header-icon">contact_support</mat-icon>
          <h3>Request Customer Edit for {{ selectedRequestForEditModal?.id }}</h3>
        </div>
        <button type="button" class="modal-close-btn" (click)="closeCustomerEditModal()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="modal-body">
        <p class="modal-intro">
          Specify what details or clarifications the customer should revise (e.g. invalid berth coordinates, draft
          restrictions, or cargo confirmation). This will set the request status to <strong>Edit Requested</strong> and
          unlock editing permissions for the customer.
        </p>

        <mat-form-field appearance="outline" class="modal-textarea-field">
          <mat-label>Inquiry / Edit Notes for Customer</mat-label>
          <textarea matInput [(ngModel)]="customerEditPromptNote" rows="4"
            placeholder="e.g. Please verify the departure draft limit and provide updated waypoint coordinates for Singapore discharge terminal."></textarea>
        </mat-form-field>
      </div>

      <div class="modal-footer">
        <button type="button" mat-stroked-button class="modal-cancel-btn" (click)="closeCustomerEditModal()">
          Cancel
        </button>
        <button type="button" mat-flat-button class="modal-confirm-btn" (click)="confirmCustomerEditRequest()">
          <mat-icon>send</mat-icon>
          <span>Send Edit Request</span>
        </button>
      </div>
    </div>
  </div>

</div>